import { Check, Timer, TrendingUp } from "lucide-react";

import { GymMap } from "./components/gym-map";
import { RestFace } from "./components/wrist";
import {
  BookingScreen,
  DayScreen,
  LiveScreen,
  ProgressScreen,
  RecoveryScreen,
  ScanScreen,
  WorkoutScreen,
  kilos,
} from "@/features/portal/components/app-screens";
import { CoachDesk } from "@/features/portal/components/coach-desk";
import { Laptop, Phone, Watch } from "@/features/portal/components/devices";
import { FloatChip, SoonBadge, Stage } from "@/features/portal/components/stage";
import { Accent, displayTitle, Eyebrow, GoLink, Section } from "@/features/portal/components/portal-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * The words that open `/features`, inside the head card, and three phones fanned out under them
 * that run off the card's bottom edge. The side phones only join from `md`, where there is room.
 */
export const FeaturesHero = () => (
  <div className="relative">
    <div className="relative mx-auto max-w-[900px] px-1 pt-14 text-center md:pt-20">
      <Eyebrow>{m.portal_features_eyebrow()}</Eyebrow>
      <h1 className={cn(displayTitle, "mt-6")}>
        {m.portal_features_title_lead()} <Accent>{m.portal_features_title_tail()}</Accent>
      </h1>
      <p className="mx-auto mt-6 max-w-[54ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.portal_features_lede()}
      </p>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <GoLink to="/signup" tone="lime">
          {m.portal_nav_sign_up()}
        </GoLink>
      </div>
    </div>
    <div className="relative mx-auto -mb-8 mt-14 h-[400px] max-w-[980px] overflow-hidden md:-mb-11 md:mt-16 md:h-[520px]">
      <span className="absolute inset-x-[10%] bottom-0 h-3/4 rounded-full bg-[radial-gradient(closest-side,var(--site-glow),transparent)] blur-2xl" />
      <Phone className="absolute top-[70px] left-[calc(50%-330px)] w-[290px] -rotate-[9deg] opacity-90 max-md:hidden">
        <ScanScreen />
      </Phone>
      <Phone className="absolute top-[70px] left-[calc(50%+40px)] w-[290px] rotate-[9deg] opacity-90 max-md:hidden">
        <ProgressScreen />
      </Phone>
      <Phone className="absolute top-0 left-1/2 w-[300px] -translate-x-1/2 md:w-[330px]">
        <DayScreen />
      </Phone>
      <FloatChip className="top-[44%] left-[calc(50%-300px)] max-md:hidden">
        <Check className="size-4 text-primary" strokeWidth={3} />
        {m.features_hero_chip_booked({ coach: m.features_sample_coach(), time: "18:00" })}
      </FloatChip>
      <FloatChip className="top-[24%] left-[calc(50%+190px)] max-md:hidden">
        <TrendingUp className="size-4 text-primary" />
        {m.features_hero_chip_record({ kg: kilos(92.5) })}
      </FloatChip>
    </div>
  </div>
);

/** A phone standing in the tile's picture area, its top showing and the rest cropped by the card. */
const croppedPhone = "absolute top-0 left-1/2 w-[min(300px,82%)] -translate-x-1/2";

/**
 * What the app does, tile by tile: each tile one feature on the hardware it runs on. Wide tiles
 * open and break the grid; the watch is marked as coming, since the watch app is not out yet.
 */
export const FeaturesShowcase = () => (
  <Section>
    <div className="mb-10 max-w-[780px] px-1">
      <Eyebrow>{m.features_showcase_eyebrow()}</Eyebrow>
      <h2 className={cn(displayTitle, "mt-5")}>
        {m.features_showcase_title_lead()} <Accent>{m.features_showcase_title_tail()}</Accent>
      </h2>
      <p className="mt-5 max-w-[56ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
        {m.features_showcase_lede()}
      </p>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      <Stage
        side
        title={m.features_train_title()}
        lead={m.features_train_lead()}
        body={m.features_train_body()}
        className="md:col-span-2"
        artClassName="flex justify-center px-6 pb-10 lg:items-center lg:py-14"
      >
        <div className="relative w-[min(300px,80%)] lg:w-[330px]">
          <Phone>
            <WorkoutScreen />
          </Phone>
          <FloatChip className="top-[30%] -left-[38%] max-lg:hidden">
            <Timer className="size-4 text-primary" />
            {m.features_train_chip_rest()}
          </FloatChip>
          <FloatChip className="top-[58%] -right-[34%] max-lg:hidden">
            <TrendingUp className="size-4 text-primary" />
            {m.features_train_chip_last({ kg: kilos(2.5) })}
          </FloatChip>
        </div>
      </Stage>

      <Stage lead={m.features_recovery_lead()} body={m.features_recovery_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <RecoveryScreen />
        </Phone>
      </Stage>

      <Stage lead={m.features_scan_lead()} body={m.features_scan_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <ScanScreen />
        </Phone>
      </Stage>

      <Stage lead={m.features_gym_lead()} body={m.features_gym_body()} className="h-[680px] md:h-[620px]">
        <GymMap />
      </Stage>

      <Stage
        lead={m.features_watch_lead()}
        body={m.features_watch_body()}
        badge={<SoonBadge>{m.features_soon()}</SoonBadge>}
        className="h-[680px] md:h-[620px]"
      >
        <Watch className="absolute top-1/2 left-1/2 w-[240px] -translate-x-1/2 -translate-y-[47%]">
          <RestFace />
        </Watch>
      </Stage>

      <Stage
        side
        title={m.features_live_title()}
        lead={m.features_live_lead()}
        body={m.features_live_body()}
        className="md:col-span-2"
        artClassName="flex items-center justify-center px-5 pt-2 pb-12 lg:py-16 lg:pr-12 lg:pl-0"
      >
        <div className="relative w-full max-w-[800px] pr-[10%]">
          <Laptop>
            <CoachDesk />
          </Laptop>
          <Phone className="absolute right-0 -bottom-[7%] w-[20%]">
            <LiveScreen />
          </Phone>
        </div>
      </Stage>

      <Stage lead={m.features_booking_lead()} body={m.features_booking_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <BookingScreen />
        </Phone>
      </Stage>

      <Stage lead={m.features_progress_lead()} body={m.features_progress_body()} className="h-[680px] md:h-[620px]" fade>
        <Phone className={croppedPhone}>
          <ProgressScreen />
        </Phone>
      </Stage>
    </div>
  </Section>
);
